import * as React from "react";
import { Link } from "react-router-dom";
import chunk from "lodash/chunk";
// components
import { IconWrapper } from "common/icons";
import PACKS from "common/icons/packs";
import translateAppURLToURL from "@vingle/wtf-sdk/helpers/translateAppURLToURL";
import { useEventCallback } from "@vingle/wtf-sdk/helpers/hook";
import PulseLoadingDot from "common/components/pulseLoadingDot";
import SideBar, { Header } from "common/components/sideBar";
// styles
const styles = require("./newlyTrending.scss");

interface IProps {
  interestCharts: Vingle.Discover.IInterestChart[];
  isLoading?: boolean;
}

const NEWLY_TRENDING_CHUNK_NUMBER = 10;

function pageDirection(page: number, direction: number, totalPage: number) {
  return (totalPage + page + direction) % totalPage;
}

function useNewlyTrending(props: IProps) {
  const { interestCharts } = props;
  const trendingRankChunk = React.useMemo(() => {
    return chunk(interestCharts, NEWLY_TRENDING_CHUNK_NUMBER);
  }, [interestCharts]);
  const [page, setPage] = React.useState(0);
  const prevPage = pageDirection(page, -1, trendingRankChunk.length);
  const nextPage = pageDirection(page, 1, trendingRankChunk.length);
  const pageSlideStyle = React.useMemo(
    () => ({
      transform: `translate3d(-${page * 100}%, 0, 0)`,
    }),
    [page],
  );
  const handleSlidePrev = useEventCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setPage(prevPage);
  });
  const handleSlideNext = useEventCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setPage(nextPage);
  });
  return {
    prevPage,
    nextPage,
    trendingRankChunk,
    handleSlidePrev,
    handleSlideNext,
    pageSlideStyle,
  };
}

export default React.memo(function NewlyTrending(props: IProps) {
  const {
    trendingRankChunk,
    handleSlideNext,
    handleSlidePrev,
    pageSlideStyle,
    prevPage,
    nextPage,
  } = useNewlyTrending(props);
  const { isLoading } = props;
  return (
    <SideBar className={styles.container}>
      <Header
        left={<h3 className={styles.title}>Newly Trending</h3>}
        right={
          <nav className={styles.nav}>
            <a
              href={`#newlyTrending__${prevPage}`}
              className={styles.navButtonPrev}
              onClick={handleSlidePrev}
            >
              <IconWrapper icon={PACKS.TRENDING_DISCOVER.RANKING_ARROW} />
            </a>
            <a
              href={`#newlyTrending__${nextPage}`}
              className={styles.navButtonNext}
              onClick={handleSlideNext}
            >
              <IconWrapper icon={PACKS.TRENDING_DISCOVER.RANKING_ARROW} />
            </a>
          </nav>
        }
      />
      <div className={styles.wrapper} style={pageSlideStyle}>
        {isLoading ? (
          <div className={styles.loading}>
            <PulseLoadingDot />
          </div>
        ) : (
          trendingRankChunk.map((chunkData, rankIndex) => {
            return (
              <ol
                id={`newlyTrending__${rankIndex}`}
                className={styles.list}
                start={rankIndex * NEWLY_TRENDING_CHUNK_NUMBER + 1}
                key={rankIndex}
              >
                {chunkData.map((item, index) => {
                  const isCommunity =
                    item.community && item.community.generation.active !== null;

                  return (
                    <li key={index} className={styles.item}>
                      <Link
                        to={translateAppURLToURL(item.url)}
                        className={styles.itemWrapper}
                      >
                        <div className={styles.ItemTitle}>
                          <IconWrapper
                            icon={
                              isCommunity
                                ? PACKS.INTEREST_MARK.SIDE_BAR.RECOMMEND
                                    .COMMUNITY_MARK
                                : PACKS.INTEREST_MARK.SIDE_BAR.RECOMMEND
                                    .INTEREST_MARK
                            }
                            className={styles.interestMark}
                          />
                          <strong>{item.title}</strong>
                        </div>
                        <span className={styles.itemDesc}>
                          {item.update ? (
                            <React.Fragment>
                              {item.update.value}
                              {item.update.type === "up" ? (
                                <em className={styles.itemUpdateUp}>{"↑"}</em>
                              ) : (
                                "↓"
                              )}
                            </React.Fragment>
                          ) : (
                            "-"
                          )}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            );
          })
        )}
      </div>
    </SideBar>
  );
});
